import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import Plus from '@mui/icons-material/Add';
import Trash2 from '@mui/icons-material/Delete';
import Lock from '@mui/icons-material/Lock';
import Split from '@mui/icons-material/CallSplit';
import { OPERATORS, OPERATOR_LABELS } from '../../constants';

const LABEL_SX = {
  width: 20,
  textAlign: 'right',
};

function LabeledField({ label, children }) {
  return (
    <Box display="flex" gap={1} alignItems="center">
      <Typography
        variant="caption"
        fontWeight="bold"
        color="text.disabled"
        sx={LABEL_SX}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function RoutesTab({
  routes,
  nodes,
  type,
  selectedNodeId,
  onAddRoute,
  onRemoveRoute,
  onCriteriaChange,
  onRemoveCriteria,
  onRouteGotoChange,
}) {
  const targetNodeOptions = (nodes || []).filter(
    n => n.id && n.id !== selectedNodeId,
  );
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="overline" fontWeight="bold" color="text.secondary">
          Output Connections
        </Typography>
        {onAddRoute && (
          <Button
            size="small"
            startIcon={<Plus sx={{ fontSize: 14 }} />}
            onClick={onAddRoute}
            sx={{ textTransform: 'none' }}>
            {type === 'SWITCH' ? 'Add Condition' : 'Add Route'}
          </Button>
        )}
      </Box>
      {routes?.length === 0 && (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'grey.100',
          }}>
          <Typography variant="caption" color="text.secondary">
            No routes defined. This is a terminal node.
          </Typography>
        </Box>
      )}
      <Box display="flex" flexDirection="column" gap={1.5}>
        {routes?.map((route, idx) => (
          <Paper
            key={route.keyId}
            variant="outlined"
            sx={{ p: 1.5, position: 'relative' }}>
            {((route.criteria && type === 'SWITCH') || !route.criteria) &&
              onRemoveRoute && (
                <IconButton
                  size="small"
                  onClick={() => onRemoveRoute(idx)}
                  className="delete-btn"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    opacity: 0.5,
                    transition: 'opacity 0.2s',
                    '&:hover': { color: 'error.main', opacity: 1 },
                    '&:focus-visible': { opacity: 1 },
                  }}
                  title="Remove Route"
                  aria-label="Remove Route">
                  <Trash2 sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            {!route.criteria && (
              <Box display="flex" flexDirection="column" gap={1.5}>
                <TextField
                  fullWidth
                  label="Trigger Signal"
                  size="small"
                  value={route.signal || ''}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Lock sx={{ fontSize: 16 }} htmlColor="#bdbdbd" />
                    ),
                    sx: {
                      bgcolor: 'grey.50',
                      fontWeight: 'bold',
                      color: 'text.secondary',
                    },
                  }}
                />
                {onRouteGotoChange && (
                  <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 0.5 }}>
                      Next node
                    </Typography>
                    <Select
                      value={route.goto ?? ''}
                      displayEmpty
                      onChange={e =>
                        onRouteGotoChange(
                          idx,
                          e.target.value === '' ? null : e.target.value,
                        )
                      }
                      sx={{ fontSize: 12 }}>
                      <MenuItem value="">
                        <em>• Disconnected</em>
                      </MenuItem>
                      {targetNodeOptions.map(n => (
                        <MenuItem key={n.id} value={n.id}>
                          {n.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}
            {route.criteria && (
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" gap={1} alignItems="center" mb={1}>
                  <Split sx={{ fontSize: 16 }} htmlColor="#f57c00" />
                  <Typography variant="caption" fontWeight="bold">
                    Switch Conditions
                  </Typography>
                </Box>
                {route.criteria.map((crit, cIdx) => (
                  <Box
                    key={crit.keyId}
                    sx={{
                      pl: 1.5,
                      borderLeft: '2px solid',
                      borderColor: 'warning.light',
                      ml: 0.5,
                      position: 'relative',
                    }}>
                    <IconButton
                      size="small"
                      onClick={() => onRemoveCriteria(idx, cIdx)}
                      className="del-crit"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: -4,
                        opacity: 0.5,
                        transition: 'opacity 0.2s',
                        zIndex: 10,
                        '&:hover': { color: 'error.main', opacity: 1 },
                        '&:focus-visible': { opacity: 1 },
                      }}
                      title="Remove Condition"
                      aria-label="Remove Condition">
                      <Trash2 sx={{ fontSize: 14 }} />
                    </IconButton>
                    <Box
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.02)',
                        p: 1,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}>
                      <LabeledField label="Op">
                        <FormControl
                          size="small"
                          sx={{ flex: 1, bgcolor: 'white' }}>
                          <Select
                            value={crit.operator || OPERATORS.EQUAL}
                            onChange={e =>
                              onCriteriaChange(
                                idx,
                                cIdx,
                                'operator',
                                e.target.value,
                              )
                            }
                            sx={{ fontSize: 12 }}>
                            {Object.entries(OPERATOR_LABELS).map(
                              ([value, label]) => (
                                <MenuItem key={value} value={value}>
                                  {label}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                      </LabeledField>
                      {![OPERATORS.IS_EMPTY, OPERATORS.NOT_EMPTY].includes(
                        crit.operator,
                      ) && (
                        <LabeledField label="Val">
                          <TextField
                            size="small"
                            sx={{ flex: 1, bgcolor: 'white' }}
                            placeholder="Value..."
                            value={crit.value || ''}
                            onChange={e =>
                              onCriteriaChange(
                                idx,
                                cIdx,
                                'value',
                                e.target.value,
                              )
                            }
                            InputProps={{ sx: { fontSize: 12 } }}
                          />
                        </LabeledField>
                      )}
                      <LabeledField label="GO">
                        <FormControl
                          size="small"
                          sx={{ flex: 1, bgcolor: 'white' }}>
                          <Select
                            value={crit.goto || ''}
                            displayEmpty
                            onChange={e =>
                              onCriteriaChange(
                                idx,
                                cIdx,
                                'goto',
                                e.target.value === '' ? null : e.target.value,
                              )
                            }
                            sx={{ fontSize: 12 }}>
                            <MenuItem value="">
                              <em>• Disconnected</em>
                            </MenuItem>
                            {nodes.map(n => (
                              <MenuItem key={n.id} value={n.id}>
                                {n.id}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </LabeledField>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

export default RoutesTab;
